<script setup lang="ts">
/**
 * RegionOverviewSection — region-level rollup cards (bead lucky-war-q5yp).
 *
 * SHARED surface: rendered on the home page AND on /branch (the funnel entry —
 * persistent region overview above the map). Extracted from the former
 * home-only RegionsSection (bead lucky-war-etcn.3) so both surfaces render
 * from one component and cannot drift.
 *
 * Cards show region AGGREGATES: sales ฿ + matched-day WoW% primary, then
 * คะแนนจาก Google / รีวิว / รีวิวเชิงลบ %, branch count + sales coverage.
 * Every card drills into /branch?region=<id> (on /branch itself that sets the
 * region deep-dive state: RegionSummaryBand + filtered list).
 *
 * Calm-state rules: regions without sales show "—" (never a fake 0%), a
 * gated WoW shows no arrow, negative % is hidden below the sample gate
 * (server-enforced), and the rating is labeled as Google's published
 * rating per the Option B transparency requirement.
 */
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import type { RegionSummary } from '@/api';
import { lensFromQuery, scopeLabel } from '@/lib/brand';
import { fmtRating, fmtBaht, fmtNum } from '@/lib/display';

const props = defineProps<{
  regionOptions: RegionSummary[];
}>();
const route = useRoute();
const lens = computed(() => lensFromQuery(route.query.brand));

const visible = computed(() =>
  props.regionOptions.filter(
    (r) => r.rating_avg != null || r.review_count > 0 || r.sales_wow != null || r.sales_net_total != null,
  ),
);

function pctClass(v: number): string {
  if (Math.abs(v) < 0.05) return 'is-flat';
  return v > 0 ? 'is-up' : 'is-down';
}

function regionQuery(regionId: string) {
  return { ...route.query, region: regionId };
}
</script>

<template>
  <section
    v-if="visible.length > 0"
    class="rs"
    :aria-label="`ผลการดำเนินงานรายภูมิภาค · ${scopeLabel(lens)}`"
  >
    <h2 class="card-head">ผลการดำเนินงานรายภูมิภาค · {{ scopeLabel(lens) }}</h2>
    <div class="rs__region-grid">
      <router-link
        v-for="r in visible"
        :key="r.region_id"
        class="rs__region-card"
        :to="{ name: 'branch-list', query: regionQuery(r.region_id) }"
        :aria-label="`ดูสาขาในภูมิภาค${r.region_name}`"
      >
        <div class="rs__region-head">
          <span class="rs__region-name">{{ r.region_name }}</span>
          <span class="rs__region-count">{{ r.branch_count }} สาขา</span>
        </div>

        <!-- Primary: weekly sales + WoW -->
        <div class="rs__region-sales">
          <template v-if="r.sales_wow">
            <span class="rs__region-sales-baht">฿{{ fmtBaht(r.sales_wow.current) }}</span>
            <span
              v-if="r.sales_wow.comparison_available"
              class="rs__region-wow"
              :class="pctClass(r.sales_wow.delta_pct)"
            >
              {{ r.sales_wow.delta_pct > 0 ? '↑' : r.sales_wow.delta_pct < 0 ? '↓' : '' }}
              {{ Math.abs(r.sales_wow.delta_pct).toFixed(1) }}%
            </span>
            <span v-else class="rs__region-wow rs__region-wow--gated">—</span>
          </template>
          <span v-else class="rs__region-sales-baht rs__region-sales-baht--na">—</span>
          <span class="rs__region-sales-label">ยอดขายสัปดาห์นี้ (ไม่รวม VAT)</span>
        </div>

        <!-- Secondary: voice KPIs -->
        <div class="rs__region-meta">
          <span class="rs__region-stat">
            <span class="rs__kpi-star" aria-hidden="true">&#9733;</span>
            {{ fmtRating(r.rating_avg) }}
            <span class="rs__region-stat-src">คะแนนจาก Google</span>
          </span>
          <span class="rs__region-stat rs__region-stat--reviews">
            {{ fmtNum(r.review_count) }} รีวิว
          </span>
          <span v-if="r.negative_pct != null" class="rs__region-stat rs__region-stat--neg">
            รีวิวเชิงลบ {{ r.negative_pct.toFixed(1) }}%
          </span>
        </div>

        <!-- Coverage -->
        <p v-if="r.branches_reporting != null && r.branches_reporting > 0" class="rs__region-coverage">
          ข้อมูลยอดขาย {{ r.branches_reporting }}/{{ r.branch_count }} สาขา
        </p>
        <span class="rs__region-drill">ดูสาขาในภูมิภาคนี้ ›</span>
      </router-link>
    </div>
    <p class="rs__source-note">ยอดขายไม่รวม VAT · คะแนนจาก Google · รีวิวเชิงลบ (%) วิเคราะห์ข้อความด้วย AI และแสดงเมื่อมีข้อมูลเพียงพอ</p>
  </section>
</template>

<style scoped>
/* ONE card per section (PO 2026-08-18): the section title is the card's
 * header with a divider, matching SectionCard / the page's KPI cards. */
.rs {
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--radius-card);
  padding: 0.85rem;
  margin-bottom: 1rem;
  box-shadow: none;
}

/* Title: global .card-head (canonical card title, 2026-08-18). */

.rs__region-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.5rem;
}

@media (min-width: 500px) {
  .rs__region-grid {
    grid-template-columns: repeat(4, 1fr);
  }
}

/* Region tiles: flattened to surface-2 tiles inside the section card
 * (were standalone cards — nested cards read inconsistent). */
.rs__region-card {
  background: var(--surface-2);
  border: 1px solid var(--line);
  border-radius: var(--radius-badge);
  padding: 0.65rem 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  cursor: pointer;
  text-align: left;
  color: var(--text);
  font: inherit;
  text-decoration: none;
  transition: border-color 0.2s, background 0.2s, box-shadow 0.2s;
  box-shadow: none;
}

.rs__region-card:hover,
.rs__region-card:focus-visible {
  border-color: var(--brand-light);
  box-shadow: none;
}

.rs__region-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.4rem;
}

.rs__region-name {
  font-size: 0.85rem;
  font-weight: 700;
}

.rs__region-count {
  font-size: 0.62rem;
  color: var(--text-muted);
  white-space: nowrap;
}

/* ── Primary sales row ─────────────────────────────────────────────────── */
.rs__region-sales {
  display: flex;
  align-items: baseline;
  gap: 0.35rem;
  flex-wrap: wrap;
}

.rs__region-sales-baht {
  font-size: 0.95rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  font-family: var(--font-heading, 'Kanit', 'Sarabun', sans-serif);
}

.rs__region-sales-baht--na {
  color: var(--text-faint);
}

.rs__region-wow {
  font-size: 0.72rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.rs__region-wow.is-up { color: var(--ok); }
.rs__region-wow.is-down { color: var(--degraded); }
.rs__region-wow.is-flat { color: var(--text-muted); }
.rs__region-wow--gated { color: var(--text-faint); font-weight: 500; }

.rs__region-sales-label {
  flex-basis: 100%;
  font-size: 0.58rem;
  color: var(--text-muted);
}

/* ── Secondary voice KPIs ──────────────────────────────────────────────── */
.rs__region-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem 0.55rem;
}

.rs__region-stat {
  font-size: 0.68rem;
  color: var(--text-dim);
  display: inline-flex;
  align-items: center;
  gap: 0.15rem;
}

.rs__region-stat-src {
  font-size: 0.56rem;
  color: var(--text-faint);
}

.rs__region-stat--neg {
  color: var(--degraded);
  font-weight: 600;
}

.rs__kpi-star { color: var(--star); font-size: 0.95rem; }

.rs__region-coverage {
  margin: 0;
  font-size: 0.58rem;
  color: var(--text-faint);
}

.rs__region-drill {
  margin-top: auto;
  font-size: 0.64rem;
  font-weight: 600;
  color: var(--brand-light);
}

.rs__source-note {
  margin: 0.55rem 0 0;
  color: var(--text-muted);
  font: var(--text-caption);
  line-height: 1.4;
}
</style>
